/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { get, isEmpty, values, filter, size } from "lodash";
import { v4 as uuidv4 } from "uuid";
import { Button, PageLoader, AlertBanner } from "glic-ui-components";
import axios from "axios";
import { Form } from "../../../../components/Form";
import { validateUserIdAlreadyExists } from "../functions";
import {
  codeSnippetFunc,
  encryptData,
  enforceFormat,
  formatToPhone,
  setDomainUrl,
} from "../../../../utils/commonFunctions";
import UsernameValidator from "../../../../components/UsernameValidator";
import { FormFields } from "./formFields";
import PasswordChecks from "../../../../components/PasswordChecks";
import { MESSAGES } from "../../../../utils/Constants/Messages";
import { GLIC_API_DOB } from "../../../../utils/API/glicAPI";
import { TermsAndConditions } from "../TermsAndConditons";
import { ErrorCodes } from "../../../../utils/Constants";
import { GoPaperless } from "../GoPaperless";
import { PHONE_NUMBER_REGEX } from "../../../../utils/Constants/CommonConstants";

const RegistrationFormComponent = ({
  identityToken: token,
  firstName,
  lastName,
  plainEmailId,
  setNextStep,
  setRegistrationData,
  dob,
  phoneNumber = "",
}) => {
  const [userId, setUserId] = useState(plainEmailId);
  const [password, setPassword] = useState("");
  const [isValidPassword, setIsValidPassword] = useState(false);
  const [isValidUsername, setIsValidUsername] = useState(false);
  const [userIdExistsError, setUserIdExistsError] = useState(false);
  const [checkBoxValues, setCheckBoxValues] = useState(false);
  const [openTermsAndConditionAccordian, setOpenTermsAndConditionAccordian] =
    useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [gaOktaEnabled, setGaOktaEnabled] = useState("");
  const [isPaperlessOptionSelected, togglePaperlessOptionSelected] =
    useState(true);
  const [userPhoneNumber, setUserPhoneNumber] = useState(
    formatToPhone(phoneNumber)
  );
  const [showErrorBanner, setShowErrorBanner] = useState("");

  useEffect(() => {
    validateUserIdAlreadyExists(userId, uuidv4(), setUserIdExistsError);
    axios({ url: "/api/config" }).then((res) => {
      setGaOktaEnabled(res?.data?.app?.ga_okta_enabled);
    });
  }, []);

  const onChange = (event) => {
    const fieldName = event.target && event.target.name;
    setShowErrorBanner("");
    if (fieldName === "userId") {
      setUserId(event.target.value);
    } else if (fieldName === "password") {
      setPassword(event.target.value);
    } else if (fieldName === "userPhoneNumber") {
      setUserPhoneNumber(formatToPhone(event.target.value));
    }
  };

  const onKeyDown = (event) => {
    const fieldName = event.target && event.target.name;
    if (fieldName === get(FormFields, "PhoneNumber.id", "")) {
      enforceFormat(event);
    }
  };
  const onKeyUp = (event) => {
    const fieldName = event.target && event.target.name;
    if (fieldName === get(FormFields, "PhoneNumber.id", "")) {
      formatToPhone(event.target.value);
    }
  };

  const onPaste = (event) => {
    const fieldName = event.target && event.target.name;
    setShowErrorBanner("");
    if (fieldName === get(FormFields, "PhoneNumber.id", "")) {
      // event.preventDefault();
      const pastedData = (event.clipboardData || window.clipboardData).getData(
        "text"
      );
      enforceFormat(event);
      setUserPhoneNumber(formatToPhone(pastedData));
    }
  };

  const checkIsValidPhoneNumber = () =>
    PHONE_NUMBER_REGEX.test(userPhoneNumber);

  const checkIsValidPassword = (isValidPassword) => {
    setIsValidPassword(isValidPassword);
  };

  const checkIsUsernameValid = (isUsernameValid) => {
    setIsValidUsername(isUsernameValid);
  };

  const onBlur = async (event) => {
    const fieldName = event.target && event.target.name;
    if (fieldName === get(FormFields, "EmailId.id", "") && !isEmpty(userId)) {
      validateUserIdAlreadyExists(userId, uuidv4(), setUserIdExistsError);
    }
  };

  const validatorComponent = {
    userId: !isEmpty(userId) && (
      <UsernameValidator
        username={userId}
        checkIsUsernameValid={checkIsUsernameValid}
        userIdExistsError={userIdExistsError}
        validationMessage={MESSAGES.UNIQUE_USERID_MESSAGE}
      />
    ),
    password: (
      <PasswordChecks
        password={password}
        checkIsValidPassword={checkIsValidPassword}
        user={userId}
        firstName={firstName}
        lastName={lastName}
        emailAddress={plainEmailId}
        validationMessage={MESSAGES.CREATE_PASSWORD_REQUIRED_MESSAGE}
        passwordComponentType="member"
        gaOktaEnabled={gaOktaEnabled}
      />
    ),
  };

  const handleRegistration = async () => {
    codeSnippetFunc('Form Step Completed', 'Submit Registration button', 'track');
    const arrayOfCheckbox = values(checkBoxValues);
    setShowErrorBanner("");
    const selectedCheckboxValues = filter(arrayOfCheckbox, "isCheckboxActive");
    if (size(selectedCheckboxValues) === 2) {
      try {
        setIsLoading(true);
        const body = {
          password: encryptData(password),
          identity_token: token,
          user_id: userId,
          db: dob,
          paperless: isPaperlessOptionSelected,
          phone_number: encryptData(userPhoneNumber.split("-").join("")),
        };
        const response = await GLIC_API_DOB.post(
          "/userRegistration/",
          body,
          true,
          {
            Accept: "application/json",
            "Content-Type": "text/plain",
            target: "checkdob",
            "ga-request-id": uuidv4()
          }
        );
        setIsLoading(false);
        // Condition to check if user enters a valid US phone number
        if (
          response?.status === 400 &&
          response?.error_code ===
            ErrorCodes.INVALID_PHONE_NUMBER_ERROR_CODE
        ) {
          setShowErrorBanner(MESSAGES.INVALID_PHONE_NUMBER_MESSAGE);
          document.getElementById("okta-common-error-banner").scrollIntoView();
        }
        // Condition to check if password is a common password based on api response
        else if (
          response?.status === 400 &&
          response?.error_code ===
            ErrorCodes.OKTA_COMMON_PASSWORD_ERROR_CODE
        ) {
          setShowErrorBanner(MESSAGES.OKTA_COMMON_PASSWORD_ERROR);
          document.getElementById("okta-common-error-banner").scrollIntoView();
        }
        // Condition to check if password contains part of username
        else if (
          response?.status === 400 &&
          response?.error_code ===
            ErrorCodes.PASSWORD_CONTAIN_USERNAME_ERROR_CODE
        ) {
          setShowErrorBanner(MESSAGES.PASSWORD_CONTAIN_USERNAME_MESSAGE);
          document.getElementById("okta-common-error-banner").scrollIntoView();
        }
        // Redirect to ID card screen
        else if (
          get(response, "isRegistrationSuccessful", false) &&
          get(response, "isTokenActive", false)
        ) {
          const idcardsUrl = setDomainUrl(window.location.host);
          window.location.href = `${idcardsUrl}/welcome/${token}/registered`;
        } else {
          setRegistrationData(response);
          setNextStep(5);
        }
      } catch (err) {
        setIsLoading(false);
        console.log(">>> err in catch", err);
        setRegistrationData(err);
        setNextStep(5);
      }
    } else {
      setOpenTermsAndConditionAccordian(true);
    }
  };

  const isDisabledRegisterButton =
    !(isValidPassword && isValidUsername && checkIsValidPhoneNumber()) ||
    isEmpty(password) ||
    isEmpty(userId) ||
    userIdExistsError ||
    showErrorBanner ||
    isEmpty(userPhoneNumber);

  return (
    <>
      <PageLoader
        show={isLoading}
        pageLoaderText={MESSAGES.PAGE_LOADER_MESSAGE}
      />
      <div className="okta-common-password-alert" id="okta-common-error-banner">
        {showErrorBanner !== "" && (
          <AlertBanner type="error" show={true}>
            {showErrorBanner}
          </AlertBanner>
        )}
      </div>
      <Form
        fields={FormFields}
        onChange={onChange}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
        onKeyUp={onKeyUp}
        validatorComponent={validatorComponent}
        values={{
          userId,
          password,
          userPhoneNumber,
        }}
      />
      <TermsAndConditions
        setCheckBoxValues={setCheckBoxValues}
        openTermsAndConditionAccordian={openTermsAndConditionAccordian}
      />
      <GoPaperless
        togglePaperlessOptionSelected={togglePaperlessOptionSelected}
      />
      <div className="justify-content-center col">
        <Button
          buttonType="submit"
          color="navy"
          onClick={handleRegistration}
          disabled={isDisabledRegisterButton}
        >
          Submit registration
        </Button>
      </div>
    </>
  );
};

export const RegistrationForm = RegistrationFormComponent;
